/**
 * Shamir Secret Sharing over GF(256) with the AES Rijndael polynomial.
 *
 * Threshold-cryptographic primitive for the dead-man-switch.
 *  - split(secret, k=3, n=5) -> 5 shares; any 3 reconstruct.
 *  - combine([share, share, share]) -> original secret.
 *
 * Encoding: each share is [x_coord:1 byte | y_coords:N bytes] = N+1 bytes.
 * Multiple bytes share the same x but different y per byte position.
 *
 * Constant-time-ish: avoids data-dependent branches in the inner loop.
 *
 * Used to encrypt crypto-wallet seed phrases. The 5 shares are then each
 * wrapped per-nominee via wrapShare() in crypto.ts, and stored in the vault.
 * On dead-man-switch release, the threshold (3) nominees coordinate to
 * unwrap their shares and recombine.
 */

const FIELD_SIZE = 256;
// GF(256) tables for AES Rijndael polynomial x^8 + x^4 + x^3 + x + 1 (0x11b)
// Generator: 3 (which IS primitive in GF(2^8) mod 0x11b; 2 is not).
const LOG: Uint8Array = new Uint8Array(FIELD_SIZE);
const EXP: Uint8Array = new Uint8Array(FIELD_SIZE);

function gfMulRaw(a: number, b: number): number {
  let result = 0;
  let aa = a & 0xff;
  let bb = b & 0xff;
  for (let i = 0; i < 8; i++) {
    if (bb & 1) result ^= aa;
    const hi = aa & 0x80;
    aa = (aa << 1) & 0xff;
    if (hi) aa ^= 0x1b;
    bb >>= 1;
  }
  return result & 0xff;
}

(() => {
  let x = 1;
  for (let i = 0; i < FIELD_SIZE - 1; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x = gfMulRaw(x, 3);
  }
  EXP[FIELD_SIZE - 1] = EXP[0] ?? 0;
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  const la = LOG[a] ?? 0;
  const lb = LOG[b] ?? 0;
  return EXP[(la + lb) % 255] ?? 0;
}

function gfDiv(a: number, b: number): number {
  if (a === 0) return 0;
  if (b === 0) throw new Error('gf divide by zero');
  const la = LOG[a] ?? 0;
  const lb = LOG[b] ?? 0;
  return EXP[(la + 255 - lb) % 255] ?? 0;
}

function evalPoly(coeffs: number[], x: number): number {
  let acc = 0;
  for (let i = coeffs.length - 1; i >= 0; i--) {
    acc = gfMul(acc, x) ^ (coeffs[i] ?? 0);
  }
  return acc & 0xff;
}

function randomBytes(n: number): Uint8Array {
  const a = new Uint8Array(n);
  crypto.getRandomValues(a);
  return a;
}

export function split(secret: Uint8Array, threshold: number, total: number): Uint8Array[] {
  if (threshold < 2 || threshold > 255) throw new Error('threshold out of range');
  if (total < threshold || total > 255) throw new Error('total out of range');
  const shares: Uint8Array[] = [];
  for (let x = 1; x <= total; x++) {
    shares.push(new Uint8Array(secret.length + 1));
    (shares[x - 1] as Uint8Array)[0] = x;
  }
  for (let byteIdx = 0; byteIdx < secret.length; byteIdx++) {
    const secretByte = secret[byteIdx] ?? 0;
    const coeffs: number[] = [secretByte];
    const rand = randomBytes(threshold - 1);
    for (let i = 0; i < threshold - 1; i++) coeffs.push(rand[i] ?? 0);
    for (let x = 1; x <= total; x++) {
      const y = evalPoly(coeffs, x);
      const share = shares[x - 1];
      if (share) share[byteIdx + 1] = y;
    }
  }
  return shares;
}

export function combine(shares: Uint8Array[]): Uint8Array {
  if (shares.length < 2) throw new Error('need at least 2 shares');
  const expectedLen = (shares[0]?.length ?? 1) - 1;
  for (const s of shares) {
    if (s.length - 1 !== expectedLen) throw new Error('share length mismatch');
  }
  const xs: number[] = [];
  for (const s of shares) xs.push(s[0] ?? 0);
  const seen = new Set<number>();
  for (const x of xs) {
    if (seen.has(x)) throw new Error('duplicate x coordinate');
    if (x === 0) throw new Error('invalid x coordinate 0');
    seen.add(x);
  }

  const out = new Uint8Array(expectedLen);
  for (let byteIdx = 0; byteIdx < expectedLen; byteIdx++) {
    let acc = 0;
    for (let i = 0; i < shares.length; i++) {
      const xi = xs[i] ?? 0;
      const yi = (shares[i] as Uint8Array)[byteIdx + 1] ?? 0;
      let num = 1;
      let den = 1;
      for (let j = 0; j < shares.length; j++) {
        if (i === j) continue;
        const xj = xs[j] ?? 0;
        num = gfMul(num, xj);
        den = gfMul(den, xj ^ xi);
      }
      acc ^= gfMul(yi, gfDiv(num, den));
    }
    out[byteIdx] = acc;
  }
  return out;
}
